/**
 * ContentRatingToggle - Animated flip-style toggle for content ratings
 *
 * Replaces the emoji-based toggle buttons with smooth flip animations.
 * Uses framer-motion for 3D flip effect when toggling.
 *
 * PRESERVES:
 * - OR-based multi-select (multiple can be active)
 * - At least one must remain selected
 * - safe/suggestive/nsfw rating system
 */

import { motion, AnimatePresence } from "framer-motion";
import { Palette, Flame, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";

type ContentRating = "safe" | "suggestive" | "nsfw";

interface ContentRatingToggleProps {
  selected: ContentRating[];
  onChange: (rating: ContentRating) => void;
  className?: string;
}

interface RatingConfig {
  label: string;
  icon: React.ElementType;
  activeColors: string;
  inactiveColors: string;
  description: string;
}

const ratingConfigs: Record<ContentRating, RatingConfig> = {
  safe: {
    label: "Safe",
    icon: Palette,
    activeColors: "bg-green-600/20 border-green-500 text-green-400",
    inactiveColors: "bg-[var(--color-bg-card)] border-[var(--color-border)] text-[var(--color-text-muted)]",
    description: "All ages",
  },
  suggestive: {
    label: "Suggestive",
    icon: Sparkles,
    activeColors: "bg-yellow-600/20 border-yellow-500 text-yellow-400",
    inactiveColors: "bg-[var(--color-bg-card)] border-[var(--color-border)] text-[var(--color-text-muted)]",
    description: "Mild themes",
  },
  nsfw: {
    label: "NSFW",
    icon: Flame,
    activeColors: "bg-red-600/20 border-red-500 text-red-400",
    inactiveColors: "bg-[var(--color-bg-card)] border-[var(--color-border)] text-[var(--color-text-muted)]",
    description: "18+ only",
  },
};

function RatingButton({
  rating,
  isSelected,
  onClick,
}: {
  rating: ContentRating;
  isSelected: boolean;
  onClick: () => void;
}) {
  const config = ratingConfigs[rating];
  const Icon = config.icon;

  return (
    <motion.button
      onClick={onClick}
      className={cn(
        "relative px-4 py-2 rounded-full text-sm font-medium transition-colors border overflow-hidden",
        isSelected ? config.activeColors : config.inactiveColors
      )}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Flip animation container */}
      <motion.div
        className="flex items-center gap-2"
        initial={false}
        animate={{
          rotateX: isSelected ? 0 : 0,
          opacity: 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
      >
        {/* Icon with pulse animation when active */}
        <motion.div
          animate={isSelected ? { scale: [1, 1.2, 1] } : { scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Icon size={16} />
        </motion.div>

        <span>{config.label}</span>

        {/* Active indicator dot */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-current"
            />
          )}
        </AnimatePresence>
      </motion.div>

      {/* Background glow effect when active */}
      <AnimatePresence>
        {isSelected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 -z-10 blur-xl"
            style={{
              background: rating === "safe"
                ? "radial-gradient(circle, rgba(34,197,94,0.3) 0%, transparent 70%)"
                : rating === "suggestive"
                ? "radial-gradient(circle, rgba(234,179,8,0.3) 0%, transparent 70%)"
                : "radial-gradient(circle, rgba(239,68,68,0.3) 0%, transparent 70%)"
            }}
          />
        )}
      </AnimatePresence>
    </motion.button>
  );
}

export function ContentRatingToggle({
  selected,
  onChange,
  className,
}: ContentRatingToggleProps) {
  return (
    <div className={cn("flex justify-center items-center gap-2 flex-wrap", className)}>
      <span className="text-sm text-[var(--color-text-muted)] mr-2">Show:</span>

      {(Object.keys(ratingConfigs) as ContentRating[]).map((rating) => (
        <RatingButton
          key={rating}
          rating={rating}
          isSelected={selected.includes(rating)}
          onClick={() => onChange(rating)}
        />
      ))}
    </div>
  );
}
